import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTransfers, useAccounts } from '@/hooks/queries/useTreasury';
import { TransferForm } from '@/features/treasury/components/TransferForm';
import { useState } from 'react';

export const Route = createFileRoute('/apps/treasury/transfers/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: TransfersPage,
});

function TransfersPage() {
  const navigate = useNavigate();
  const { company } = useAuthStore();
  const { data: transfers = [], isLoading, refetch } = useTransfers(company?.id || '');
  const { data: accounts = [] } = useAccounts(company?.id || '');
  const [showTransferForm, setShowTransferForm] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-DZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getAccountName = (accountId: string) => {
    const account = accounts.find((a) => a.id === accountId);
    return account?.name || 'Compte supprimé';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: '/apps/treasury' })}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              التحويلات / Transferts
            </h1>
            <p className="mt-2 text-gray-600">
              {transfers.length} transfert(s) entre comptes
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refetch}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button
              onClick={() => setShowTransferForm(true)}
              className="bg-gradient-to-r from-purple-500 to-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau transfert
            </Button>
          </div>
        </div>

        {/* Transfers Table */}
        <Card>
          <CardHeader>
            <CardTitle>Historique des transferts</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">
                Chargement...
              </div>
            ) : transfers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ArrowRight className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="font-medium">Aucun transfert encore</p>
                <p className="text-sm mt-1">Créez votre premier transfert entre comptes</p>
                <Button
                  onClick={() => setShowTransferForm(true)}
                  className="mt-4 bg-gradient-to-r from-purple-500 to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau transfert
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        N° Transfert
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        De
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        →
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Vers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Frais
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transfers.map((transfer) => (
                      <tr key={transfer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(transfer.transfer_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.transfer_number}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {getAccountName(transfer.from_account_id)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <ArrowRight className="w-5 h-5 text-purple-600 mx-auto" />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {getAccountName(transfer.to_account_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {transfer.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-purple-600">
                          {formatCurrency(transfer.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {transfer.transfer_fee > 0 ? formatCurrency(transfer.transfer_fee) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              transfer.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {transfer.status === 'completed' ? 'Terminé' : transfer.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transfer Form */}
        <TransferForm
          open={showTransferForm}
          onClose={() => {
            setShowTransferForm(false);
            refetch();
          }}
        />
      </div>
    </DashboardLayout>
  );
}
